import React, { useEffect, useState } from "react";
import { apiFetch } from "../api";

export default function VideoUpload({ ruleset = "high_school", title = "Video Evaluation" }) {
  const [videoFile, setVideoFile] = useState(null);
  const [foulType, setFoulType] = useState("");   // now stores the selected rule filename
  const [notes, setNotes] = useState("");
  const [ruleOptions, setRuleOptions] = useState([]);
  const [loadingRules, setLoadingRules] = useState(true);
  const [err, setErr] = useState("");
  const rulesetLabel = {
    high_school: "high school",
    college: "college",
    pro: "pro",
  }[ruleset] || "selected";

  // Fetch rule options from backend
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingRules(true);
        const params = new URLSearchParams({ ruleset });
        const res = await apiFetch(`/rules/list?${params.toString()}`);
        const data = await res.json();
        if (!cancelled) {
          if (Array.isArray(data)) {
            setRuleOptions(data);
          } else {
            setErr(data?.error || "Failed to load rules.");
          }
        }
      } catch (e) {
        if (!cancelled) setErr(String(e));
      } finally {
        if (!cancelled) setLoadingRules(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ruleset]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!videoFile) {
      alert("Please select a video first.");
      return;
    }
    if (!foulType) {
      alert("Please select a foul type (rule).");
      return;
    }

    const formData = new FormData();
    formData.append("file", videoFile);        // must be "file"
    formData.append("foul_type", foulType);    // value is the rule filename (e.g., "holding.txt")
    formData.append("notes", notes);
    formData.append("ruleset", ruleset);

    try {
      const res = await apiFetch(`/upload`, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) {
        const text = await res.text();
        console.error("Upload failed:", res.status, text);
        alert("Upload failed.");
        return;
      }
      const data = await res.json();
      console.log("Upload success:", data);
      alert("Upload successful!");
      setVideoFile(null);
      setNotes("");
      setFoulType("");
      // refreshUploads();  // refresh uploads immediately (uncomment if function is defined)
      // Start polling uploads every 10 seconds after successful upload
      const pollInterval = setInterval(() => {
        console.log("Polling uploads... (replace this with actual fetch logic)");
      }, 10000);
      setTimeout(() => clearInterval(pollInterval), 60000); // stop polling after 1 minute
    } catch (err) {
      console.error("Upload error:", err);
      alert("Upload failed.");
    }
  };

  return (
    <section className="toolPanel">
      <h2>{title}</h2>

      {err && <div style={{ color: "crimson", marginBottom: 12 }}>Error: {err}</div>}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Video</label>
          <input
            type="file"
            accept="video/*"
            onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>
            Foul Type (Rule)
          </label>
          {loadingRules ? (
            <div>Loading rules...</div>
          ) : (
            <select
              value={foulType}
              onChange={(e) => setFoulType(e.target.value)}
              style={{ width: "100%", padding: 8 }}
            >
              <option value="">Select a rule</option>
              {ruleOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          )}
          <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
            These options come from the {rulesetLabel} rules table.
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            style={{ width: "100%", padding: 8, resize: "vertical" }}
            placeholder="Optional notes"
          />
        </div>

        <button type="submit" style={{ padding: "10px 16px", borderRadius: 8 }}>
          Upload
        </button>
      </form>
    </section>
  );
}
